import React, { useCallback, useContext } from 'react';
import { Icon, Input, message } from 'antd';
import * as S from '../styles';
import { useBatchJob } from '../../../../../context/BatchJobContext';
import { useTranslation } from 'react-i18next';
import { BATCH_JOBS_DASHBOARD as trans } from '../translationValues';
import { useSessionContext } from '../../../../../context/sessionContext';
import { checkUserAccessRoles, isFeatureEnabled } from '../../../../../utils/helper';
import { AppStoreContext } from '../../../../../context/appStoreContext';

const NAMESPACE = ['BATCH_JOBS_DASHBOARD'];

const Step1BatchInfo = () => {
  const {
    jobName,
    setJobName,
    jobType,
    setJobType,
    format,
    setFormat,
    setPage,
    setDataCount,
    setFetchedTemplates,
    setSearchQuery,
    setShowLanguageDropdown,
    setSelectedLanguage,
    setLanguageSearchQuery,
    setToEmails,
    setCcEmails,
    setToEmailInput,
    setCcEmailInput,
    setToPhoneNumbers,
    setToPhoneNumberInput,
    setFilteredLanguages,
    initialFetchRef,
    setSelectedTemplate,
    setUploadedFile,
    draftBatchInfo,
  } = useBatchJob();

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);
  const { isOrganizationSignedIn } = useContext(AppStoreContext);

  const hasFormUserRole = checkUserAccessRoles(userDetails, ['FORMUSER']);

  const isDraft = draftBatchInfo !== undefined;

  const { t } = useTranslation(NAMESPACE);

  const resetState = () => {
    setSelectedTemplate(null);
    setPage(0);
    setDataCount(0);
    setFetchedTemplates([]);
    setSearchQuery('');
    setShowLanguageDropdown(false);
    setSelectedLanguage(null);
    setLanguageSearchQuery('');
    setToEmails([]);
    setCcEmails([]);
    setToEmailInput('');
    setCcEmailInput('');
    setToPhoneNumbers([]);
    setToPhoneNumberInput('');
    setFilteredLanguages([]);
    initialFetchRef.current = true;
    setUploadedFile(null);
  };

  const handleJobTypeChange = type => {
    setFormat('PRINT');
    setJobType(type);
    resetState();
  };

  const handleFormatChange = ch => {
    setFormat(ch);
    resetState();
  };

  const getJobTypeDescription = type => {
    switch (type) {
      case 'STANDARD':
        return t(trans.STANDARD_DESC.key, trans.STANDARD_DESC.value);
      case 'FORM':
        return t(trans.FORM_DESC.key, trans.FORM_DESC.value);
      case 'ADVANCED':
        return t(trans.ADVANCED_DESC.key, trans.ADVANCED_DESC.value);
      default:
        return '';
    }
  };

  const getJobTypeLabel = type => {
    switch (type) {
      case 'STANDARD':
        return t(trans.STANDARD_TYPE.key, trans.STANDARD_TYPE.value);
      case 'FORM':
        return t(trans.FORM_TYPE.key, trans.FORM_TYPE.value);
      case 'ADVANCED':
        return t(trans.ADVANCED_TYPE.key, trans.ADVANCED_TYPE.value);
      default:
        return type;
    }
  };

  return (
    <S.StepContent>
      <S.FormContainer isStepOne={true}>
        <S.StepHeader>
          <S.StepHeaderTitle>
            {t(trans.BATCH_INFORMATION_TITLE.key, trans.BATCH_INFORMATION_TITLE.value)}
          </S.StepHeaderTitle>
          <S.StepSubtitle>
            {t(trans.BATCH_INFORMATION_SUBTITLE.key, trans.BATCH_INFORMATION_SUBTITLE.value)}
          </S.StepSubtitle>
        </S.StepHeader>
        <div style={{ marginBottom: '16px' }}>
          <S.Label>
            {t(trans.JOB_NAME_LABEL.key, trans.JOB_NAME_LABEL.value)}{' '}
            {jobType !== 'FORM' && <S.Required>*</S.Required>}
          </S.Label>
          <Input
            placeholder={
              jobType === 'FORM'
                ? ''
                : t(trans.JOB_NAME_PLACEHOLDER.key, trans.JOB_NAME_PLACEHOLDER.value)
            }
            value={jobType === 'FORM' ? '' : jobName}
            onChange={e => setJobName(e.target.value)}
            size="large"
            disabled={jobType === 'FORM'}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <S.Label>{t(trans.JOB_TYPE_LABEL.key, trans.JOB_TYPE_LABEL.value)}</S.Label>
          <S.Grid cols={3}>
            {(hasFormUserRole && (isOrganizationSignedIn ? isFeatureEnabled('Forms', userDetails) : true) ? ['STANDARD', 'FORM', 'ADVANCED'] : ['STANDARD', 'ADVANCED']).map(type => (
              <S.OptionCard
                key={type}
                selected={jobType === type}
                onClick={() => {
                  if (isDraft) return;
                  handleJobTypeChange(type);
                }}
                disabled={isDraft && (type === 'STANDARD' || type === 'FORM')}
              >
                {jobType === type && (
                  <S.CheckBadge>
                    <Icon type="check" style={{ fontSize: '12px' }} />
                  </S.CheckBadge>
                )}
                <S.IconWrapper>
                  <Icon
                    type={
                      type === 'STANDARD'
                        ? 'thunderbolt'
                        : type === 'FORM'
                        ? 'file-text'
                        : 'setting'
                    }
                    style={{ fontSize: '20px' }}
                  />
                </S.IconWrapper>
                <S.CardTitle>{getJobTypeLabel(type)}</S.CardTitle>
                <S.CardDesc>{getJobTypeDescription(type)}</S.CardDesc>
              </S.OptionCard>
            ))}
          </S.Grid>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <S.Label>
            {t(trans.DELIVERY_CHANNEL_LABEL.key, trans.DELIVERY_CHANNEL_LABEL.value)}{' '}
            <S.Required>*</S.Required>
          </S.Label>
          <S.Grid cols={2}>
            {(jobType === 'FORM'
              ? ['PRINT', 'EMAIL', 'SMS', 'CALL']
              : ['PRINT', 'EMAIL', 'MULTI CHANNEL']
            ).map(ch => {
              const channelKeyMap = {
                PRINT: trans.CHANNEL_PRINT,
                EMAIL: trans.CHANNEL_EMAIL,
                SMS: trans.CHANNEL_SMS,
                CALL: trans.CHANNEL_CALL,
                'MULTI CHANNEL': trans.CHANNEL_MULTI_CHANNEL,
              };
              const channelTrans = channelKeyMap[ch];
              return (
              <S.ChannelButton
                key={ch}
                selected={format === ch}
                onClick={() => handleFormatChange(ch)}
              >
                  {channelTrans ? t(channelTrans.key, channelTrans.value).toUpperCase() : ch}
              </S.ChannelButton>
              );
            })}
          </S.Grid>
        </div>
      </S.FormContainer>
    </S.StepContent>
  );
};

export default Step1BatchInfo;
